import {NgModule} from "@angular/core";
import {RouterModule, Routes} from "@angular/router";
import {AssetDetailsPageComponent} from "./asset/asset-details-page/asset-details-page.component";
import {AssetOverviewPageComponent} from "./asset/asset-overview-page/asset-overview-page.component";
import {FundDetailsPageComponent} from "./fund/fund-details-page/fund-details-page.component";
import {FundOverviewPageComponent} from "./fund/fund-overview-page/fund-overview-page.component";
import {AuthRouteBuilder} from "./shared/services/login/auth-route-builder";
import {DealOverviewPageComponent} from "./deal/deal-overview-page/deal-overview-page.component";
import {DealDetailsPageComponent} from "./deal/deal-details-page/deal-details-page.component";
import {FundAiSetupViewerPageComponent} from "./fund-ai-setup/fund-ai-setup-viewer-page/fund-ai-setup-viewer-page.component";
import {AlgorithmicDmvViewerPageComponent} from "./algorithmic-dmv/algorithmic-dmv-viewer-page/algorithmic-dmv-viewer-page.component";
import {GeneralPartnerOverviewPageComponent} from "./fund/general-partner/general-partner-overview-page/general-partner-overview-page.component";
import {GeneralPartnerDetailsPageComponent} from "./fund/general-partner/general-partner-details-page/general-partner-details-page.component";
import {AuthGuard} from "./shared/services/login/auth.guard";
import {unsavedChangesGuard} from "./shared/guards/unsaved-changes.guard";

import {ReleaseNotesPageComponent} from "./shared/components/release-notes-page/release-notes-page.component";
import {FundValuationOverviewPageComponent} from "./fund/fund-valuation-overview-page/fund-valuation-overview-page.component";
import {DocumentationPageComponent} from "./shared/components/documentation-page/documentation-page.component";
import {provideState} from "@ngrx/store";
import {fvSensitivityReducer, fvSensitivityStoreFeatureKey} from "./fund/fund-valuation-sensitivity-page/store/fv-sensitivity.reducer";
import {dealSensitivityReducer, dealSensitivityStoreFeatureKey} from "./deal/deal-sensitivity-page/store/deal-sensitivity.reducer";
import * as dealSensitivityEffects from "./deal/deal-sensitivity-page/store/deal-sensitivity.effects";
import {provideEffects} from "@ngrx/effects";
import * as fvSensitivityEffects from "./fund/fund-valuation-sensitivity-page/store/fv-sensitivity.effects";

const routes: Routes = [
    {
        path: "",
        canActivate: [AuthGuard],
        children: [
            {
                path: "",
                redirectTo: "funds",
                pathMatch: "full"
            },
            {
                path: "deals/:dealId",
                component: DealDetailsPageComponent,
            },
            {
                path: "deals",
                component: DealOverviewPageComponent,
            },
            {
                path: "funds/:fundId",
                component: FundDetailsPageComponent
            },
            {
                path: "funds",
                component: FundOverviewPageComponent,
            },
            {
                path: "fund-valuation-overview",
                component: FundValuationOverviewPageComponent,
            },
            {
                path: "gp/:gpId",
                component: GeneralPartnerDetailsPageComponent
            },
            {
                path: "gp",
                component: GeneralPartnerOverviewPageComponent,
            },
            {
                path: "assets/:assetId",
                component: AssetDetailsPageComponent
            },
            {
                path: "assets",
                component: AssetOverviewPageComponent
            },
            {
                path: "fund-ai-setup/:taskInfoId",
                component: FundAiSetupViewerPageComponent,
            },
            {
                path: "algorithmic-dmv/:taskInfoId",
                component: AlgorithmicDmvViewerPageComponent,
            },
            {
                path: "import",
                loadChildren: () =>
                    import("./import/import.module").then(m => m.ImportModule),
            },
            {
                path: "release-notes",
                component: ReleaseNotesPageComponent
            },
            {
                path: "documentation",
                component: DocumentationPageComponent
            },
            {
                path: "pattern-group",
                loadComponent: () =>
                    import("./shared/components/pattern-group/pattern-group-page/pattern-group-page.component")
                        .then(m => m.PatternGroupPageComponent),
            },
            {
                path: "settings",
                canActivate: [AuthGuard],
                canDeactivate: [unsavedChangesGuard],
                loadComponent: () =>
                    import("./settings/settings-page/settings-page.component")
                        .then(m => m.SettingsPageComponent),
            },
            {
                path: "funds/:fundId/valuation/sensitivity",
                providers: [
                    provideState(fvSensitivityStoreFeatureKey, fvSensitivityReducer),
                    provideEffects(fvSensitivityEffects),
                ],
                children: [
                    {
                        path: "",
                        loadComponent: () =>
                            import("./fund/fund-valuation-sensitivity-page/fund-valuation-sensitivity-page.component")
                                .then(m => m.FundValuationSensitivityPageComponent),
                    },
                    {
                        path: ":sensitivityId",
                        loadComponent: () =>
                            import("./fund/fund-valuation-sensitivity-page/fv-sensitivity-details-page/fv-sensitivity-details-page.component")
                                .then(m => m.FvSensitivityDetailsPageComponent),
                    },
                ],
            },
            {
                path: "deals/:dealId/sensitivity",
                providers: [
                    provideState(dealSensitivityStoreFeatureKey, dealSensitivityReducer),
                    provideEffects(dealSensitivityEffects),
                ],
                children: [
                    {
                        path: "",
                        loadComponent: () =>
                            import("./deal/deal-sensitivity-page/deal-sensitivity-page.component")
                                .then(m => m.DealSensitivityPageComponent),
                    },
                    {
                        path: ":sensitivityId",
                        loadComponent: () =>
                            import("./deal/deal-sensitivity-page/deal-sensitivity-details-page/deal-sensitivity-details-page.component")
                                .then(m => m.DealSensitivityDetailsPageComponent),
                    },
                ],
            },
        ]
    },
    ...AuthRouteBuilder.authRoutes()
];

@NgModule({
    imports: [RouterModule.forRoot(routes, {
        // set "paramsInheritanceStrategy" to be able to access parent-route params and data in child routes
        paramsInheritanceStrategy: "always"
    })],
    exports: [RouterModule]
})
export class AppRoutingModule {
}
